// Accessibility patch for keyboard focus management on the OCV in-app feedback
// form (e.g. "Give a compliment - What did you like?").
//
// When the user activates "Submit" while a required field (such as the
// "What did you like?" comment) is empty, the remotely loaded Centro SDK renders
// an inline validation error but leaves keyboard focus on the Submit button. A
// keyboard or screen-reader user is never taken to the field they need to fix,
// which violates WCAG 2.4.3 (Focus Order) / 3.3.1 (Error Identification): after a
// failed submit, focus must move to the first field in error.
//
// Because the markup comes from a remotely loaded SDK bundle and the errors are
// only rendered after the SDK finishes its own validation/re-render, we watch the
// feedback container, bind a listener to the Submit button, and once a submit
// attempt surfaces validation errors we move focus to the first invalid control.

const SUBMIT_LISTENER_FLAG = 'rlOcvSubmitFocusBound';

// Fluent UI v9 marks the offending control with `aria-invalid="true"` and links
// its validation message. Centro submit buttons expose a `submit` test id; we
// also fall back to native submit buttons and a "Submit" text match.
const INVALID_CONTROL_SELECTOR = '[aria-invalid="true"]';
const FLUENT_FIELD_SELECTOR = '.fui-Field';
const FLUENT_ERROR_MESSAGE_SELECTOR = '.fui-Field__validationMessage';
const FOCUSABLE_CONTROL_SELECTOR = 'input, textarea, select, [contenteditable="true"], [tabindex]';

// After a submit attempt the SDK re-validates and re-renders asynchronously. We
// wait until the DOM stops mutating (settles into its final validated state)
// before choosing the focus target, so a stale `aria-invalid` left over from a
// previous submit can't win over the field that is actually invalid this time.
const SETTLE_MS = 100;
const MAX_WAIT_MS = 1000;

type BoundButton = HTMLButtonElement & { [SUBMIT_LISTENER_FLAG]?: boolean };

let activeObserver: MutationObserver | null = null;
// Cleanup for an in-flight "focus after settle" operation, so a rapid re-submit
// cancels the previous pending focus move instead of racing it.
let pendingFocusCleanup: (() => void) | null = null;

const isElementVisible = (element: HTMLElement): boolean => {
  if (element.hidden || element.getAttribute('aria-hidden') === 'true') {
    return false;
  }
  // `offsetParent` is null for `display: none` elements (and fixed elements, but
  // the feedback controls are not fixed-positioned), and the client rects are
  // empty for zero-size elements.
  return element.offsetParent !== null || element.getClientRects().length > 0;
};

// Resolves the control that keyboard focus should land on for a given error
// marker. `aria-invalid` normally sits on the control itself, but when it sits on
// a wrapper we look for the nearest focusable descendant instead.
const resolveFocusTarget = (element: HTMLElement): HTMLElement | null => {
  if (element.matches(FOCUSABLE_CONTROL_SELECTOR) || element.tabIndex >= 0) {
    return element;
  }
  const descendant = element.querySelector<HTMLElement>(FOCUSABLE_CONTROL_SELECTOR);
  return descendant ?? null;
};

// Returns the first invalid, visible control in document order, or null when no
// validation error is currently rendered.
const findFirstInvalidControl = (root: ParentNode): HTMLElement | null => {
  const invalid = Array.from(root.querySelectorAll<HTMLElement>(INVALID_CONTROL_SELECTOR));
  for (const element of invalid) {
    if (!isElementVisible(element)) {
      continue;
    }
    const target = resolveFocusTarget(element);
    if (target && isElementVisible(target)) {
      return target;
    }
  }

  // Fallback: some fields signal errors via a Fluent validation message without
  // `aria-invalid` on the control. Focus the control of the first such field.
  const fields = Array.from(root.querySelectorAll<HTMLElement>(FLUENT_FIELD_SELECTOR));
  for (const field of fields) {
    const message = field.querySelector(FLUENT_ERROR_MESSAGE_SELECTOR);
    if (!message || !message.textContent?.trim()) {
      continue;
    }
    const control = field.querySelector<HTMLElement>(FOCUSABLE_CONTROL_SELECTOR);
    if (control && isElementVisible(control)) {
      return control;
    }
  }

  return null;
};

const cancelPendingFocus = (): void => {
  if (pendingFocusCleanup) {
    pendingFocusCleanup();
    pendingFocusCleanup = null;
  }
};

// Waits for the SDK's post-submit validation/re-render to settle (no DOM
// mutations for SETTLE_MS, or MAX_WAIT_MS elapsed), then moves focus to the first
// field that is invalid in that final state. Waiting for the DOM to settle is
// what prevents a stale `aria-invalid` from a previous submit from stealing focus
// before the SDK clears it and marks the field that is actually invalid now.
//
// `scope` is the specific form/dialog that owns the activated Submit button, so
// discovery of invalid controls (and mutation observation) is confined to the
// feedback form and never reaches unrelated controls elsewhere on the page.
const focusFirstInvalidAfterSettle = (scope: ParentNode): void => {
  cancelPendingFocus();

  const observationRoot: Node = (scope as Document).body ?? (scope as unknown as Node);
  const deadline = Date.now() + MAX_WAIT_MS;
  let settleTimer: ReturnType<typeof setTimeout> | null = null;
  let maxTimer: ReturnType<typeof setTimeout> | null = null;
  let done = false;

  const finish = (): void => {
    if (done) {
      return;
    }
    const target = findFirstInvalidControl(scope);
    // If validation hasn't surfaced an error yet and we still have time, keep
    // waiting — the SDK may re-render more than once. The MAX_WAIT_MS cap is
    // enforced by `maxTimer` below, so this can never wait indefinitely.
    if (!target && Date.now() < deadline) {
      settleTimer = setTimeout(finish, SETTLE_MS);
      return;
    }
    done = true;
    cleanup();
    if (target && document.activeElement !== target) {
      target.focus();
    }
  };

  // Restart the settle window on each mutation, but never past the hard deadline
  // so continuous mutations cannot postpone the focus move forever.
  const bump = (): void => {
    if (done) {
      return;
    }
    if (settleTimer) {
      clearTimeout(settleTimer);
    }
    const remaining = deadline - Date.now();
    if (remaining <= 0) {
      finish();
      return;
    }
    settleTimer = setTimeout(finish, Math.min(SETTLE_MS, remaining));
  };

  const observer = new MutationObserver(bump);
  observer.observe(observationRoot, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['aria-invalid', 'hidden', 'class'],
  });

  const cleanup = (): void => {
    observer.disconnect();
    if (settleTimer) {
      clearTimeout(settleTimer);
      settleTimer = null;
    }
    if (maxTimer) {
      clearTimeout(maxTimer);
      maxTimer = null;
    }
    if (pendingFocusCleanup === cleanup) {
      pendingFocusCleanup = null;
    }
  };

  pendingFocusCleanup = cleanup;
  // Hard cap: force a final decision once MAX_WAIT_MS elapses regardless of
  // ongoing mutations, so the observer is always disconnected.
  maxTimer = setTimeout(finish, MAX_WAIT_MS);
  // Start the initial settle window; subsequent SDK mutations extend it.
  bump();
};

const isSubmitButton = (button: HTMLButtonElement): boolean => {
  const testId = button.getAttribute('data-testid') ?? '';
  if (/submit/i.test(testId)) {
    return true;
  }
  if (button.type === 'submit') {
    return true;
  }
  const text = (button.textContent ?? '').trim().toLowerCase();
  return text === 'submit';
};

// The Centro feedback modal is rendered as a portal/overlay (mirroring the
// existing `feedbackA11y` patch, which also searches at the document level), so
// the Submit button is discovered document-wide. To avoid ever focusing controls
// outside the feedback form, the focus search itself is scoped to the button's
// own form/dialog subtree.
const getSubmitScope = (button: HTMLButtonElement): ParentNode =>
  button.closest('[role="dialog"]') ?? button.closest('form') ?? button.ownerDocument;

// Binds a submit listener to any Submit button rendered within the feedback
// modal. On activation it schedules the focus move so it runs after the SDK
// validates and re-renders, scoped to that button's form/dialog.
const bindSubmitListeners = (root: ParentNode): void => {
  const buttons = root.querySelectorAll<BoundButton>('button');
  buttons.forEach((button) => {
    if (button[SUBMIT_LISTENER_FLAG] || !isSubmitButton(button)) {
      return;
    }
    button[SUBMIT_LISTENER_FLAG] = true;
    button.addEventListener('click', () => {
      focusFirstInvalidAfterSettle(getSubmitScope(button));
    });
  });
};

/**
 * Ensures that, after the user activates "Submit" on the OCV in-app feedback form
 * with invalid data, keyboard focus moves to the first field in error. Safe to
 * call on every feedback launch; only one observer is kept active at a time.
 */
export const enforceFeedbackErrorFocus = (container: HTMLElement): void => {
  activeObserver?.disconnect();

  // The Centro SDK renders the feedback modal as a portal/overlay rather than
  // inside `container`, so Submit buttons must be discovered at the document
  // level (the existing `feedbackA11y` patch relies on the same behavior). Focus
  // is still confined to each button's own form/dialog via `getSubmitScope`, so
  // this does not reach unrelated controls elsewhere on the page.
  const searchRoot: ParentNode = container.ownerDocument ?? container;

  // Bind to anything already rendered, then keep watching for the SDK to render
  // or re-render the Submit button.
  bindSubmitListeners(searchRoot);

  activeObserver = new MutationObserver(() => {
    bindSubmitListeners(searchRoot);
  });
  const observationRoot: Node = container.ownerDocument?.body ?? container;
  activeObserver.observe(observationRoot, { childList: true, subtree: true });
};

/** Stops observing the feedback container. Exposed primarily for testing/cleanup. */
export const stopEnforcingFeedbackErrorFocus = (): void => {
  activeObserver?.disconnect();
  activeObserver = null;
  cancelPendingFocus();
};
